import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import { useGetStripeAccountStatus } from '@/api/endpoints/stripeConnect/useGetStripeAccountStatus';
import { useGetStripeDashboardLink } from '@/api/endpoints/stripeConnect/useGetStripeDashboardLink';
import { useOnboardStripeConnect } from '@/api/endpoints/stripeConnect/useOnboardStripeConnect';
import { useTimeoutState } from '@/hooks/useTimeoutState';

import { LoadingButton } from '@/components/common/LoadingButton';

const StripeConnectSettings: React.FC = () => {
  const { data: status, isLoading: isLoadingStatus, error } = useGetStripeAccountStatus();
  const { mutate: onboard, isPending: isOnboarding } = useOnboardStripeConnect();
  const { mutate: getDashboardLink, isPending: isLoadingDashboardLink } =
    useGetStripeDashboardLink();

  const isOnboardingLoading = useTimeoutState(isOnboarding, 5000);
  const isDashboardLinkLoading = useTimeoutState(isLoadingDashboardLink, 5000);

  const handleOnboard = () => {
    onboard();
  };

  const handleViewDashboard = () => {
    getDashboardLink();
  };

  const renderContent = () => {
    if (isLoadingStatus) {
      return <CircularProgress size={24} />;
    }

    if (error || !status) {
      return <Typography color="error">Could not load Stripe account status.</Typography>;
    }

    let statusText = 'Not Connected';
    let canOnboard = true;
    let canViewDashboard = false;

    if (status.onboardingComplete && status.chargesEnabled && status.payoutsEnabled) {
      statusText = 'Active';
      canOnboard = false;
      canViewDashboard = true;
    } else if (status.detailsSubmitted || status.onboardingComplete) {
      statusText = 'Onboarding Incomplete / Verification Pending';
      canOnboard = true;
      canViewDashboard = false;
    }

    return (
      <Stack
        spacing={2}
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Typography>Status: {statusText}</Typography>
        {canOnboard && (
          <LoadingButton
            variant="contained"
            onClick={handleOnboard}
            isLoading={isOnboardingLoading}
            loadingText="Connecting..."
            disabled={isOnboardingLoading || isDashboardLinkLoading} // Keep disabled logic based on both
            size="small"
            sx={{
              minWidth: 120, // optional width constraint
              px: 1.5,
              py: 0.75,
              fontSize: '0.75rem',
            }}
          >
            Connect Stripe Account
          </LoadingButton>
        )}
        {canViewDashboard && (
          <LoadingButton
            variant="outlined"
            onClick={handleViewDashboard}
            isLoading={isDashboardLinkLoading}
            loadingText="Loading..."
            disabled={isOnboardingLoading || isDashboardLinkLoading} // Keep disabled logic based on both
            size="small"
            sx={{
              minWidth: 120, // optional width constraint
              px: 1.5,
              py: 0.75,
              fontSize: '0.75rem',
            }}
          >
            View Stripe Dashboard
          </LoadingButton>
        )}
      </Stack>
    );
  };

  return (
    <Card>
      <CardHeader
        title="Stripe Payouts"
        subheader="Connect your Stripe account to receive rent payments."
      />
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box>{renderContent()}</Box>
      </CardContent>
    </Card>
  );
};

export default StripeConnectSettings;
